"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { fetchWithCSRFToken } from "@/hooks/use-csrf"

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  subject: z.string().min(1, { message: "Please select a subject." }),
  message: z.string().min(10, { message: "Message must be at least 10 characters." }),
})

type FormData = z.infer<typeof formSchema>

export function ContactForm() {
  const t = useTranslations('contact.form')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const defaultSubject = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('subject') ?? ''
    : '';

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
        name: "",
        email: "",
        subject: defaultSubject,
        message: ""
    }
  })

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    
    try {
      const response = await fetchWithCSRFToken('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send message');
      }

      toast.success(t('success'));
      reset();
    } catch {
      toast.error("Failed to send message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('name')}</Label>
              <Input id="name" {...register("name")} placeholder={t('namePlaceholder')} />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input id="email" type="email" {...register("email")} placeholder={t('emailPlaceholder')} />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subject">{t('subject')}</Label>
            <Select defaultValue={defaultSubject || undefined} onValueChange={(val) => setValue("subject", val)}>
              <SelectTrigger>
                <SelectValue placeholder={t('subjectPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="early-access">{t('subjects.earlyAccess')}</SelectItem>
                <SelectItem value="sales">{t('subjects.sales')}</SelectItem>
                <SelectItem value="support">{t('subjects.support')}</SelectItem>
                <SelectItem value="general">{t('subjects.general')}</SelectItem>
              </SelectContent>
            </Select>
            {errors.subject && <p className="text-sm text-destructive">{errors.subject.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t('message')}</Label>
            <Textarea id="message" {...register("message")} placeholder={t('messagePlaceholder')} rows={5} />
            {errors.message && <p className="text-sm text-destructive">{errors.message.message}</p>}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('submitting') : t('submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
